import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface BusinessHourRow {
  day_of_week: number; // 0=Sun ... 6=Sat
  start_time: string;  // "HH:MM:SS"
  end_time: string;    // "HH:MM:SS"
  is_available: boolean;
}

export interface FormattedHourLine {
  label: string;       // e.g. "Mon – Fri" or "Saturday"
  value: string;       // e.g. "9:00 AM – 7:00 PM" or "Closed"
  isClosed: boolean;
}

const DAY_NAMES_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatTime = (t: string): string => {
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return m === 0 ? `${h}:00 ${period}` : `${h}:${mStr} ${period}`;
};

/**
 * Group consecutive days that share the same hours so the footer can read
 * "Mon – Fri: 9:00 AM – 7:00 PM" instead of listing every day.
 */
const groupHours = (hours: BusinessHourRow[]): FormattedHourLine[] => {
  // Order Mon..Sun (more natural for a public hours list)
  const order = [1, 2, 3, 4, 5, 6, 0];
  const sorted = order
    .map((dow) => hours.find((h) => h.day_of_week === dow))
    .filter((h): h is BusinessHourRow => Boolean(h));

  const lines: FormattedHourLine[] = [];
  let i = 0;
  while (i < sorted.length) {
    const cur = sorted[i];
    const curKey = cur.is_available ? `${cur.start_time}|${cur.end_time}` : "closed";

    let j = i;
    while (j + 1 < sorted.length) {
      const next = sorted[j + 1];
      const nextKey = next.is_available ? `${next.start_time}|${next.end_time}` : "closed";
      if (nextKey !== curKey) break;
      j++;
    }

    const startDow = cur.day_of_week;
    const endDow = sorted[j].day_of_week;
    const label =
      i === j
        ? DAY_NAMES_LONG[startDow]
        : `${DAY_NAMES_SHORT[startDow]} – ${DAY_NAMES_SHORT[endDow]}`;

    lines.push({
      label,
      value: cur.is_available ? `${formatTime(cur.start_time)} – ${formatTime(cur.end_time)}` : "Closed",
      isClosed: !cur.is_available,
    });

    i = j + 1;
  }
  return lines;
};

export const useBusinessHours = () => {
  const [hours, setHours] = useState<BusinessHourRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data } = await supabase
        .from("booking_hours")
        .select("day_of_week, start_time, end_time, is_available")
        .order("day_of_week");
      if (!mounted) return;
      setHours((data as BusinessHourRow[]) || []);
      setLoading(false);
    };
    load();

    // Live-update when admin edits hours
    const channel = supabase
      .channel("business-hours")
      .on("postgres_changes", { event: "*", schema: "public", table: "booking_hours" }, load)
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  return { hours, formatted: groupHours(hours), loading };
};
